import classNames from 'classnames';
import kebabCase from 'lodash/kebabCase';
import * as React from 'react';
import type { JSX } from 'react';
import styled from 'styled-components';

import { generateTestId } from '../../lib/test-helpers';
import { colors } from '../../theme/colors';
import { Spinner } from '../spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'dangerous' | 'transparent' | 'rebrand';

type Props = BaseButtonProps | (BaseButtonProps & AnchorButtonProps);

export type AnchorButtonProps = {
  href: string;
  disabled: false;
  htmlProps?: React.PropsWithoutRef<JSX.IntrinsicElements['a']>;
};

export type BaseButtonProps = {
  id?: string;
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  href?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => unknown;
  type?: React.PropsWithoutRef<JSX.IntrinsicElements['button']['type']>;
  size?: ButtonVariant;
  target?: string;
  minWidth?: number | string;
  testId?: string;
  elementRef?: React.RefObject<HTMLElement | null>;
  innerRef?: (el: HTMLElement | null) => void;
  htmlProps?: React.PropsWithoutRef<JSX.IntrinsicElements['button']>;
  form?: string;
  className?: string;
};

interface ButtonTheme {
  textColor: string;
  borderColor: string;
  backgroundColor: string;
  backgroundHoverColor: string;
  borderHoverColor: string;
  textHoverColor: string;
  backgroundDisabledColor: string;
  borderDisabledColor: string;
  textDisabledColor: string;
  textLoadingColor: string;
  focusShadowColor: string;
}

export const primaryButtonTheme: ButtonTheme = {
  textColor: 'white',
  borderColor: colors.brand[400],
  backgroundColor: colors.brand[400],
  backgroundHoverColor: colors.brand[300],
  borderHoverColor: colors.brand[300],
  textHoverColor: 'white',
  backgroundDisabledColor: colors.steel[200],
  borderDisabledColor: colors.steel[200],
  textDisabledColor: 'white',
  textLoadingColor: 'white',
  focusShadowColor: `0 0 0 3px ${colors.brand[300]}70`,
};

export const secondaryButtonTheme: ButtonTheme = {
  textColor: colors.steel[500],
  borderColor: colors.steel[200],
  backgroundColor: 'white',
  backgroundHoverColor: 'white',
  borderHoverColor: colors.brand[300],
  textHoverColor: colors.steel[500],
  backgroundDisabledColor: colors.steel[100],
  borderDisabledColor: colors.steel[200],
  textDisabledColor: colors.steel[400],
  textLoadingColor: colors.steel[500],
  focusShadowColor: `0 0 0 3px ${colors.brand[300]}70`,
};

const tertiaryButtonTheme: ButtonTheme = {
  textColor: colors.steel[500],
  borderColor: colors.brand[100],
  backgroundColor: colors.brand[100],
  backgroundHoverColor: colors.brand[100],
  borderHoverColor: colors.brand[100],
  textHoverColor: colors.steel[500],
  backgroundDisabledColor: colors.steel[200],
  borderDisabledColor: colors.steel[200],
  textDisabledColor: 'white',
  textLoadingColor: 'white',
  focusShadowColor: `0 0 0 3px ${colors.brand[200]}70`,
};

export const dangerousButtonTheme: ButtonTheme = {
  textColor: 'white',
  borderColor: colors.fire[500],
  backgroundColor: colors.fire[500],
  backgroundHoverColor: colors.fire[400],
  borderHoverColor: colors.fire[400],
  textHoverColor: 'white',
  backgroundDisabledColor: colors.steel[200],
  borderDisabledColor: colors.steel[200],
  textDisabledColor: 'white',
  textLoadingColor: 'white',
  focusShadowColor: `0 0 0 3px ${colors.fire[300]}70`,
};

const transparentButtonTheme: ButtonTheme = {
  textColor: colors.steel[500],
  borderColor: 'transparent',
  backgroundColor: 'transparent',
  backgroundHoverColor: 'transparent',
  borderHoverColor: 'transparent',
  textHoverColor: colors.steel[500],
  backgroundDisabledColor: 'transparent',
  borderDisabledColor: 'transparent',
  textDisabledColor: colors.steel[400],
  textLoadingColor: colors.steel[500],
  focusShadowColor: `none`,
};

const rebrandButtonTheme: ButtonTheme = {
  textColor: 'white',
  borderColor: colors.rebrand[400],
  backgroundColor: colors.rebrand[400],
  backgroundHoverColor: colors.rebrand[300],
  borderHoverColor: colors.rebrand[300],
  textHoverColor: 'white',
  backgroundDisabledColor: colors.steel[200],
  borderDisabledColor: colors.steel[200],
  textDisabledColor: 'white',
  textLoadingColor: 'white',
  focusShadowColor: `0 0 0 3px ${colors.brand[300]}70`,
};

function getTheme(size: ButtonVariant): ButtonTheme {
  if (size === 'primary') return primaryButtonTheme;
  if (size === 'tertiary') return tertiaryButtonTheme;
  if (size === 'dangerous') return dangerousButtonTheme;
  if (size === 'transparent') return transparentButtonTheme;
  if (size === 'rebrand') return rebrandButtonTheme;
  return secondaryButtonTheme;
}

/**
 * @deprecated Use Button from core-ui/v2 instead
 */
export function Button(props: Props): JSX.Element {
  const {
    id,
    children,
    disabled,
    href,
    loading,
    onClick,
    testId: propTestId,
    size = 'primary',
    style,
    target,
    type,
    minWidth = 'auto',
    elementRef,
    innerRef,
    className,
  } = props;

  const testId =
    propTestId ?? (typeof children === 'string' ? generateTestId(kebabCase(children.toString()), 'button') : undefined);

  const parsedClassName = classNames(
    {
      'ui-button': true,
      disabled,
      loading,
      primary: size === 'primary',
      secondary: size === 'secondary',
    },
    className,
  );

  const spinner = loading ? <Spinner height={40} /> : null;

  const theme = getTheme(size);

  return (
    <>
      {shouldRenderAnchor(props) ? (
        <StyledAnchor
          minWidth={minWidth}
          theme={theme}
          id={id}
          data-testid={testId}
          href={href}
          onClick={onClick}
          className={parsedClassName}
          style={style}
          target={target}
          // eslint-disable-next-line react/jsx-props-no-spreading, react/destructuring-assignment
          {...props.htmlProps}
        >
          {spinner}
          <span className="text">{children}</span>
        </StyledAnchor>
      ) : (
        <StyledButton
          // eslint-disable-next-line react/jsx-props-no-spreading, react/destructuring-assignment
          {...props.htmlProps}
          minWidth={minWidth}
          theme={theme}
          id={id}
          data-testid={testId}
          type={type}
          onClick={onClick}
          className={parsedClassName}
          style={style}
          disabled={disabled || loading}
          ref={(elementRef as React.RefObject<HTMLButtonElement>) || innerRef}
        >
          {spinner}
          <span className="text">{children}</span>
        </StyledButton>
      )}
    </>
  );
}

export const sharedButtonStyles = (theme: ButtonTheme, minWidth?: number | string): string => `
  all: initial;
  appearance: none;
  background-color: ${theme.backgroundColor};
  border-radius: 4px;
  border: 1px solid ${theme.borderColor};
  box-sizing: border-box;
  color: ${theme.textColor} !important;
  cursor: pointer;
  display: inline-block;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  height: 40px;
  line-height: 38px;
  padding: 0 32px;
  position: relative;
  text-decoration: none;
  white-space: nowrap;
  text-align: center;
  min-width: ${typeof minWidth === 'number' ? `${minWidth}px` : minWidth};

  &:focus,
  &:active {
    outline: none;
    text-decoration: none !important;
  }
  &:disabled {
    cursor: not-allowed;
    outline: none;
    box-shadow: none;
  }

  &.loading,
  &.loading:active {
    color: ${theme.textLoadingColor};
    cursor: wait;
    box-shadow: none;
  }
  &.loading .text {
    color: transparent;
  }

  &:hover:not(:disabled) {
    background: ${theme.backgroundHoverColor};
    border-color: ${theme.borderHoverColor};
    color: ${theme.textHoverColor};
    text-decoration: none !important;
  }
  &.disabled {
    background-color: ${theme.backgroundDisabledColor};
    border-color: ${theme.borderDisabledColor};
    color: ${theme.textDisabledColor};
  }
  &:focus:not(:disabled),
  &:active:not(:disabled) {
    box-shadow: ${theme.focusShadowColor};
  }

  &.secondary.disabled {
    opacity: 0.6;
  }
`;

const StyledButton = styled.button<{ minWidth: number | string }>`
  ${({ minWidth, theme }) => sharedButtonStyles(theme as ButtonTheme, minWidth)}
`;

const StyledAnchor = styled.a<{ minWidth: number | string }>`
  ${({ minWidth, theme }) => sharedButtonStyles(theme as ButtonTheme, minWidth)}
`;

const shouldRenderAnchor = (props: Props): props is BaseButtonProps & AnchorButtonProps => {
  return Boolean(props.href && !props.disabled);
};
